import { Box, Button, Container, IconButton, MenuItem, TextField, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Stack } from '@mui/system';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import Iconify from 'src/components/iconify';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';
import { paths } from 'src/routes/paths';
import { useGetAccountCalendarQuery, useGetOrgCalendarsQuery, useLazyGetCalendarEventsByMonthQuery } from 'src/services/calendar/calendar.service';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import CalendarViewSkeleton from './components/calendar-view-skeleton';
import CalendarEventEditor from './modal/calendar-event-editor';
import CalendarGridView from './view/calendar-grid-view';
import CalendarListView from './view/calendar-list-view';

// ----------------------------------------------------------------------

export default function CalendarView() {

  const { t } = useTranslation();

  const theme = useTheme();

  const authContext = useAuthContext();

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState(searchParams.get('view') || 'grid');
  const [openEventEditor, setOpenEventEditor] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | undefined>(undefined);
  const [selectedCalendarId, setSelectedCalendarId] = useState<string | undefined>(undefined);

  const [month, setMonth] = useState<number>(new Date().getMonth() + 1); // 1-indexed month
  const [year, setYear] = useState<number>(new Date().getFullYear());

  const { data: myCalendar, isLoading, error: calendarLoadError } = useGetAccountCalendarQuery();

  const { data: orgCalendars } = useGetOrgCalendarsQuery(undefined, {
    skip: !authContext || ![AccountRole.Admin, AccountRole.Op_Manager].includes(authContext.account.org_role)
  });

  const [getEventsByMonth] = useLazyGetCalendarEventsByMonthQuery();

  const handleOpenEventEditor = (eventId?: string) => {
    setSelectedEventId(eventId);
    setOpenEventEditor(true);
  };

  const handleCloseEventEditor = () => {
    setOpenEventEditor(false);
    setSelectedEventId(undefined);
  };
  const handleChangeView = (newValue: string) => {
    setView(newValue);

    // Update URL params while preserving other existing params
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('view', newValue);
    setSearchParams(newSearchParams);
  };

  useEffect(() => {
    setSelectedCalendarId(myCalendar?.id);
  }, [myCalendar]);

  const handleMonthChange = (m: number, y: number) => {
    setMonth(m);
    setYear(y);
  }

  // Update view if URL params change externally
  useEffect(() => {
    const viewParam = searchParams.get('view');
    if (viewParam && (viewParam === 'grid' || viewParam === 'list') && viewParam !== view) {
      setView(viewParam);
    }
  }, [searchParams, view]);

  const switchCalendar = (calendarId: string) => {
    setSelectedCalendarId(calendarId);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('calendarId', calendarId);
    setSearchParams(newSearchParams);
  }

  const handleRefreshCalendar = async () => {
    try {
      if (selectedCalendarId) {
        await getEventsByMonth({ calendarId: selectedCalendarId, month, year }).unwrap();
        enqueueSnackbar(t('calendar.refresh_success'), { variant: 'success' });
      }
    }
    catch (error) {
      console.error('Error refreshing calendar:', error);
    }
  }

  if (isLoading) {
    return <CalendarViewSkeleton />;
  }

  if (calendarLoadError || !myCalendar) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error" gutterBottom>
          {t('calendar.error.loading')}
        </Typography>
        <Typography variant="body2">
          {t('calendar.error.try_again')}
        </Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth={false}>

      <Stack sx={{ mb: 3 }} direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: "start", md: 'center' }} justifyContent="space-between" spacing={1} width="100%">

        <CustomBreadcrumbs
          sx={{ pt: 2, width: '100%' }}
          heading={t(`calendar.title`)}
          links={[
            { name: t('common.dashboard'), href: paths.dashboard.root },
            {
              name: t(`calendar.title`),
              href: `${paths.dashboard.notetaker.root}`
            }
          ]}
        />

        <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} sx={{ width: { xs: '100%' } }}>

          <Box>
            <Tooltip title={t('common.refresh')} arrow>
              <IconButton
                onClick={handleRefreshCalendar}
              >
                <Iconify icon="tabler:refresh" sx={{ cursor: 'pointer' }} />
              </IconButton>

            </Tooltip>
          </Box>

          {
            [AccountRole.Admin, AccountRole.Op_Manager].includes(authContext.account.org_role) && (
              <TextField
                select
                value={selectedCalendarId || ''}
                onChange={(e) => switchCalendar(e.target.value)}
                size='small'
                label={t('calendar.select_calendar.label')}
                inputProps={{ 'aria-label': 'Select Calendar' }}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value={myCalendar.id} key={-1}>--</MenuItem>
                {
                  orgCalendars?.map((orgCalendar) => (
                    <MenuItem key={orgCalendar.id} value={orgCalendar.id}>
                      {`${orgCalendar.owner?.first_name} ${orgCalendar.owner?.last_name}'s calendar`}
                    </MenuItem>
                  ))
                }
              </TextField>
            )
          }

          <Button
            onClick={() => handleChangeView(view === 'grid' ? 'list' : 'grid')}
            startIcon={<Iconify icon={view === 'grid' ? 'mdi:view-list' : 'mdi:view-grid'} />}
          >
            {t('calendar.switch_view')}
          </Button>


          <Button
            size='small'
            onClick={() => handleOpenEventEditor()}
            variant="contained"
            startIcon={<Iconify icon="mdi:plus" />}
            sx={{ lineHeight: 1 }}
          >
            {t('calendar.editor.new_event')}
          </Button>
        </Stack>
      </Stack>

      <Box>
        {view === 'grid' ? (
          <CalendarGridView calendarId={selectedCalendarId || myCalendar.id} onMonthChange={handleMonthChange} />
        ) : (
          <CalendarListView calendarId={selectedCalendarId || myCalendar.id} />
        )}
      </Box>

      {myCalendar && (
        <CalendarEventEditor
          calendarId={selectedCalendarId || myCalendar.id}
          eventId={selectedEventId}
          open={openEventEditor}
          onClose={handleCloseEventEditor}
        />
      )}

    </Container>
  );
}